import React, { useState } from 'react';
import {
  FiX as X,
  FiClock as Clock,
  FiAlertCircle as AlertCircle,
  FiCheckCircle as CheckCircle,
  FiSend as Send,
  FiXCircle,
} from 'react-icons/fi';
import { supportAPI } from '../services/realApi';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { PERMISSIONS } from '../utils/permissions';

interface StatusOption {
  value: string;
  label: string;
  color: string;
}

const STATUS_OPTIONS: StatusOption[] = [
  { value: 'open', label: 'Open', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' },
  {
    value: 'in_progress',
    label: 'In Progress',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
  },
  {
    value: 'resolved',
    label: 'Resolved',
    color: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
  },
  { value: 'closed', label: 'Closed', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' },
];

interface Reply {
  id?: string;
  message: string;
  created_at: string;
  user_name?: string;
  user_email?: string;
}

export interface Ticket {
  id: string;
  subject: string;
  description: string;
  status: string;
  priority: string;
  requestType: string;
  supportChannel: string;
  created_at: string;
  replies?: Reply[];
}

interface TicketDetailProps {
  ticket: Ticket;
  onClose: () => void;
  onUpdate?: () => void;
}

interface LocalReply {
  message: string;
}

const TicketDetail: React.FC<TicketDetailProps> = ({ ticket, onClose, onUpdate }) => {
  const { user } = useAuth();
  const { hasPermission, isAdmin } = usePermissions();
  const { isAWSUser } = useModuleAccess();
  const [replyText, setReplyText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localReplies, setLocalReplies] = useState<LocalReply[]>([]);
  const [error, setError] = useState('');
  const [currentStatus, setCurrentStatus] = useState(ticket.status?.toLowerCase() ?? 'open');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  // Suppress unused variable warning — user is available for future use
  void user;

  const hasPartnerAccess =
    hasPermission(PERMISSIONS.PARTNERS_VIEW) || hasPermission(PERMISSIONS.PARTNERS_MANAGE);
  const canManageStatus =
    isAdmin || hasPermission(PERMISSIONS.ADMIN_VIEW) || hasPartnerAccess || isAWSUser();

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
      case 'high':
        return 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300';
      case 'medium':
        return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
      case 'low':
        return 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const getStatusIcon = (status: string): React.ReactElement => {
    const normalizedStatus = status?.toLowerCase();
    if (normalizedStatus === 'open') return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    if (normalizedStatus === 'in_progress') return <Clock className="w-4 h-4 text-blue-500" />;
    if (normalizedStatus === 'resolved') return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (normalizedStatus === 'closed') return <FiXCircle className="w-4 h-4 text-gray-500" />;
    return <AlertCircle className="w-4 h-4 text-blue-500" />;
  };

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === currentStatus || isUpdatingStatus) return;

    setIsUpdatingStatus(true);
    setStatusMessage('');

    try {
      const result = await supportAPI.updateSupportRequest(ticket.id, { status: newStatus });
      if (result.success) {
        setCurrentStatus(newStatus);
        setStatusMessage('Status updated successfully');
        setTimeout(() => setStatusMessage(''), 3000);
        if (onUpdate) {
          onUpdate();
        }
      } else {
        setStatusMessage('Failed to update status: ' + (result.error ?? 'Unknown error'));
      }
    } catch (err: unknown) {
      console.error('Error updating status:', err);
      setStatusMessage('Failed to update status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Get replies from structured array (if available) or fallback to empty array
  const serverReplies: Reply[] = Array.isArray(ticket.replies) ? ticket.replies : [];

  // Format optimistic replies to match server format
  const formattedLocalReplies: Reply[] = localReplies.map((reply, index) => ({
    id: `local-${Date.now()}-${index}`,
    message: reply.message,
    created_at: new Date().toISOString(),
    user_name: 'You',
    user_email: '',
  }));

  // Combine server replies with local optimistic replies
  const allReplies = [...serverReplies, ...formattedLocalReplies];

  const handleReply = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!replyText.trim() || isSubmitting) return;

    const newReply: LocalReply = { message: replyText.trim() };

    // Optimistically add reply to UI
    setLocalReplies((prev) => [...prev, newReply]);
    const messageToSend = replyText.trim();
    setReplyText('');
    setIsSubmitting(true);
    setError('');

    try {
      const result = await supportAPI.replyToSupportRequest(ticket.id, { message: messageToSend });
      if (!result.success) {
        // Remove optimistic reply on failure
        setLocalReplies((prev) => prev.filter((r) => r !== newReply));
        setReplyText(messageToSend);
        setError('Failed to send reply. Please try again.');
      }
    } catch (err: unknown) {
      console.error('Error sending reply:', err);
      // Remove optimistic reply on error
      setLocalReplies((prev) => prev.filter((r) => r !== newReply));
      setReplyText(messageToSend);
      setError('Failed to send reply. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            {getStatusIcon(ticket.status)}
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{ticket.subject}</h2>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
              {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)} Priority
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Ticket Info */}
          <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Type:</span>
              <span className="ml-2 text-gray-900 dark:text-white">{ticket.requestType}</span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Channel:</span>
              <span className="ml-2 text-gray-900 dark:text-white">{ticket.supportChannel}</span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Status:</span>
              {canManageStatus ? (
                <select
                  value={currentStatus}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={isUpdatingStatus}
                  className="ml-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-gray-900 dark:text-white disabled:opacity-50"
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="ml-2 text-gray-900 dark:text-white">{currentStatus}</span>
              )}
              {isUpdatingStatus && <span className="ml-2 text-xs text-gray-500">Updating...</span>}
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Created:</span>
              <span className="ml-2 text-gray-900 dark:text-white">
                {new Date(ticket.created_at).toLocaleString()}
              </span>
            </div>
          </div>

          {/* Status update message */}
          {statusMessage && (
            <div
              className={`mb-4 p-3 rounded-md text-sm ${
                statusMessage.includes('success')
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                  : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
              }`}
            >
              {statusMessage}
            </div>
          )}

          {/* Original Request */}
          <div className="mb-6">
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">Original Request</h3>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{ticket.description}</p>
            </div>
          </div>

          {/* Replies */}
          {allReplies.length > 0 && (
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-3">
                Responses ({allReplies.length})
              </h3>
              <div className="space-y-4">
                {allReplies.map((reply) => (
                  <div
                    key={reply.id ?? `reply-${reply.created_at}`}
                    className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border-l-4 border-blue-500"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm text-blue-700 dark:text-blue-300 font-medium">
                        {reply.user_name ?? 'Support Agent'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(reply.created_at).toLocaleString()}
                      </div>
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{reply.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {allReplies.length === 0 && (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No responses yet</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">We&apos;ll respond to your ticket soon</p>
            </div>
          )}

          {/* Reply Form */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">Add Reply</h3>
            {error && (
              <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}
            <form onSubmit={handleReply}>
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your reply..."
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={4}
                disabled={isSubmitting}
              />
              <div className="flex justify-end mt-3">
                <button
                  type="submit"
                  disabled={!replyText.trim() || isSubmitting}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-md transition-colors"
                >
                  <Send className="w-4 h-4" />
                  <span>{isSubmitting ? 'Sending...' : 'Send Reply'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetail;
